// src/pages/GalleryPage.js
import React, { useState, useEffect } from 'react';

const galleryAssetContext = require.context('../assets/reading-towers/gallery', false, /\.(jpg)$/);
const img = (filename) => galleryAssetContext(`./${filename}`);

const image = (filename, alt, fullFilename = filename) => ({
  src: img(filename),
  full: img(fullFilename),
  alt,
});

const galleryGridData = [
  {
    type: 'double',
    images: [
      image('exterior-dsc4864-1400.jpg', 'Reading Tower exterior at 601 Penn Street', 'exterior-dsc4864-2800.jpg'),
      image('exterior-dsc4870-1400.jpg', 'Reading Tower exterior view', 'exterior-dsc4870-2800.jpg'),
    ],
  },
  {
    type: 'double',
    images: [
      image('foyer-1400.jpg', 'Reading Tower foyer', 'foyer-2800.jpg'),
      image('hope-1400.jpg', 'Reading Tower common area', 'hope-2800.jpg'),
    ],
  },
  {
    type: 'single',
    images: [
      image('exterior-dsc4873-1400.jpg', 'Reading Tower exterior from Penn Street', 'exterior-dsc4873-2800.jpg'),
    ],
  },
  {
    type: 'double',
    images: [
      image('x04-living-kitchen-1536.jpg', 'Reading Tower staged living and kitchen area'),
      image('x04-kitchen-1536.jpg', 'Reading Tower staged kitchen'),
    ],
  },
  {
    type: 'double',
    images: [
      image('x04-bedroom-1536.jpg', 'Reading Tower staged bedroom'),
      image('x04-back-bedroom-1536.jpg', 'Reading Tower staged second bedroom'),
    ],
  },
  {
    type: 'double',
    images: [
      image('x10-living-1536.jpg', 'Reading Tower staged living room'),
      image('x10-studio-1536.jpg', 'Reading Tower staged residence'),
    ],
  },
  {
    type: 'double',
    images: [
      image('x10-bedroom-1536.jpg', 'Reading Tower staged bedroom'),
      image('x11-bedroom-1536.jpg', 'Reading Tower staged bedroom view'),
    ],
  },
  {
    type: 'single',
    images: [
      image('north-view-1400.jpg', 'View from Reading Tower', 'north-view-2048.jpg'),
    ],
  },
  {
    type: 'double',
    images: [
      image('x11-living-1536.jpg', 'Reading Tower staged living room'),
      image('x11-bedroom-alt-1536.jpg', 'Reading Tower staged bedroom'),
    ],
  },
  {
    type: 'double',
    images: [
      image('x17-living-1536.jpg', 'Reading Tower staged living room'),
      image('x17-kitchen-1536.jpg', 'Reading Tower staged kitchen'),
    ],
  },
  {
    type: 'double',
    images: [
      image('x17-bedroom-1536.jpg', 'Reading Tower staged bedroom'),
      image('x17-second-bedroom-1536.jpg', 'Reading Tower staged second bedroom'),
    ],
  },
  {
    type: 'double',
    images: [
      image('x19-living-1536.jpg', 'Reading Tower staged living room'),
      image('x19-kitchen-1536.jpg', 'Reading Tower staged kitchen'),
    ],
  },
  {
    type: 'double',
    images: [
      image('x19-bathroom-1536.jpg', 'Reading Tower staged bathroom'),
      image('x20-bathroom-1536.jpg', 'Reading Tower staged bathroom'),
    ],
  },
  {
    type: 'double',
    images: [
      image('x20-living-1536.jpg', 'Reading Tower staged living room'),
      image('x20-kitchen-1536.jpg', 'Reading Tower staged kitchen'),
    ],
  },
  {
    type: 'double',
    images: [
      image('x17-bedroom-alt-1536.jpg', 'Reading Tower staged bedroom'),
      image('south-view-1400.jpg', 'View from Reading Tower'),
    ],
  },
  {
    type: 'double',
    images: [
      image('exterior-dsc4876-1400.jpg', 'Reading Tower exterior view', 'exterior-dsc4876-2800.jpg'),
      image('exterior-dsc4890-1400.jpg', 'Reading Tower exterior detail', 'exterior-dsc4890-2800.jpg'),
    ],
  },
];

const GalleryImage = ({ item, className, sizes, onZoom }) => (
  <img
    src={item.src}
    alt={item.alt}
    sizes={sizes}
    className={className}
    loading="lazy"
    decoding="async"
    onClick={() => onZoom(item.full)}
  />
);

const GalleryPage = () => {
  const primaryColor = 'text-teal-700';
  const [zoomSrc, setZoomSrc] = useState(null);
  const hero = image('exterior-dsc4890-1400.jpg', 'Reading Tower exterior at 601 Penn Street', 'exterior-dsc4890-2800.jpg');

  useEffect(() => {
    document.body.style.overflow = zoomSrc ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [zoomSrc]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        setZoomSrc(null);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  return (
    <div className="bg-white">
      <section className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div className="space-y-4 text-center md:text-left">
            <p className={`text-sm font-semibold uppercase tracking-wider ${primaryColor}`}>GALLERY</p>
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-1400 leading-tight">The Reading Tower Lifestyle</h1>
            <p className="text-xl text-gray-700">Downtown Reading / City Views / Modern Residences</p>
            <p className="text-xl text-gray-700">Updated Interiors / Central Location / Everyday Convenience</p>
            <p className="text-sm italic text-gray-500 pt-4">
              Click any image below to view it fullscreen.
            </p>
          </div>
          <div className="w-full">
            <img
              src={hero.src}
              srcSet={`${hero.src} 1400w, ${hero.full} 2800w`}
              sizes="(min-width: 768px) 50vw, 100vw"
              alt={hero.alt}
              className="w-full h-auto md:h-[350px] lg:h-[450px] object-cover rounded-lg shadow-xl"
              loading="eager"
              decoding="async"
            />
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-4 md:space-y-6">
          {galleryGridData.map((row, idx) => (
            <div key={idx}>
              {row.type === 'single' && (
                <GalleryImage
                  item={row.images[0]}
                  sizes="100vw"
                  className="w-full h-auto md:h-[60vh] object-cover rounded-lg shadow-lg align-bottom cursor-zoom-in"
                  onZoom={setZoomSrc}
                />
              )}

              {row.type === 'double' && (
                <div className="flex flex-col sm:flex-row gap-4 md:gap-6">
                  {row.images.map((item) => (
                    <div key={item.src} className="w-full sm:w-1/2">
                      <GalleryImage
                        item={item}
                        sizes="(min-width: 640px) 50vw, 100vw"
                        className="w-full h-64 sm:h-80 md:h-96 object-cover rounded-lg shadow-lg cursor-zoom-in"
                        onZoom={setZoomSrc}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      {zoomSrc && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
          onClick={() => setZoomSrc(null)}
        >
          <div className="relative">
            <button
              onClick={(e) => { e.stopPropagation(); setZoomSrc(null); }}
              className="absolute top-2 right-2 w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 flex items-center justify-center bg-black bg-opacity-50 rounded-full text-white z-10"
              aria-label="Close zoom"
            >
              x
            </button>
            <img
              src={zoomSrc}
              alt="Zoomed"
              className="max-w-[90vw] max-h-[90vh] rounded-lg object-contain"
              loading="eager"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryPage;
